import {
  Boxes,
  Command,
  KeyRound,
  Laptop,
  LoaderCircle,
  Search,
  UserRound,
  Wrench,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/useDebounce";
import { useGlobalSearch } from "@/hooks/useGlobalSearch";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import type { GlobalSearchCategory, GlobalSearchGroup, GlobalSearchResult } from "@/types/global-search";

const categoryIcons = {
  equipamentos: Laptop,
  colaboradores: UserRound,
  suprimentos: Boxes,
  licencas: KeyRound,
  manutencoes: Wrench,
} satisfies Record<GlobalSearchCategory, typeof Laptop>;

function statusStyle(status?: string | null) {
  const value = status?.toLowerCase() ?? "";
  if (["ativo", "ativa", "disponivel", "normal", "concluida"].includes(value)) return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
  if (["atrasado", "critico", "baixo", "em_manutencao", "aguardando_peca"].includes(value)) return "bg-amber-500/12 text-amber-700 dark:text-amber-300";
  if (["inativo", "inativa", "baixado", "cancelado", "erro"].includes(value)) return "bg-destructive/10 text-destructive";
  return "bg-muted text-muted-foreground";
}

function SearchResultRow({ result, selected, onSelect }: { result: GlobalSearchResult; selected: boolean; onSelect: () => void }) {
  const Icon = categoryIcons[result.category];
  return (
    <button
      id={`global-result-${result.category}-${result.id}`}
      type="button"
      role="option"
      aria-selected={selected}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left outline-none transition-colors",
        selected ? "bg-primary/10 text-foreground" : "hover:bg-muted/80",
      )}
    >
      <span className={cn("grid size-9 shrink-0 place-items-center rounded-lg", selected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
        <Icon className="size-[17px]" />
      </span>
      <span className="min-w-0 flex-1">
        <strong className="block truncate text-sm font-semibold">{result.title}</strong>
        <small className="block truncate text-xs text-muted-foreground">{result.description}</small>
      </span>
      {result.status && <Badge className={cn("hidden shrink-0 sm:inline-flex", statusStyle(result.status))}>{result.status.replaceAll("_", " ")}</Badge>}
    </button>
  );
}

function SearchPanel({
  groups,
  loading,
  error,
  term,
  selectedKey,
  expanded,
  onExpand,
  onSelect,
}: {
  groups: GlobalSearchGroup[];
  loading: boolean;
  error: boolean;
  term: string;
  selectedKey?: string;
  expanded: boolean;
  onExpand: () => void;
  onSelect: (result: GlobalSearchResult) => void;
}) {
  const hasMore = groups.some((item) => item.results.length > 4);

  if (term.trim().length < 2) {
    return <div className="px-5 py-8 text-center text-sm text-muted-foreground"><Search className="mx-auto mb-3 size-7 opacity-45" />Digite pelo menos dois caracteres para pesquisar.</div>;
  }
  if (loading) {
    return <div className="flex items-center justify-center gap-2 px-5 py-10 text-sm text-muted-foreground"><LoaderCircle className="size-4 animate-spin text-primary" />Pesquisando nos módulos...</div>;
  }
  if (error) {
    return <div className="px-5 py-8 text-center text-sm text-destructive">Não foi possível realizar a pesquisa. Tente novamente.</div>;
  }
  if (!groups.length) {
    return <div className="px-5 py-8 text-center text-sm text-muted-foreground">Nenhum resultado encontrado para “{term}”.</div>;
  }

  return (
    <div role="listbox" aria-label="Resultados da pesquisa" className="max-h-[min(62vh,520px)] overflow-y-auto p-2">
      {groups.map((group) => {
        const Icon = categoryIcons[group.category];
        const visible = expanded ? group.results : group.results.slice(0, 4);
        return (
          <section className="mb-2 last:mb-0" key={group.category}>
            <div className="flex items-center gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              <Icon className="size-3.5" />{group.label}<span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-[10px]">{group.results.length}</span>
            </div>
            {visible.map((result) => {
              const resultKey = `${result.category}-${result.id}`;
              return <SearchResultRow key={resultKey} result={result} selected={resultKey === selectedKey} onSelect={() => onSelect(result)} />;
            })}
          </section>
        );
      })}
      {hasMore && !expanded && (
        <button type="button" onClick={onExpand} className="mt-1 flex h-10 w-full items-center justify-center rounded-lg border border-dashed text-sm font-semibold text-primary transition hover:bg-primary/5">
          Ver todos os resultados
        </button>
      )}
    </div>
  );
}

export function GlobalSearch() {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const desktopInput = useRef<HTMLInputElement>(null);
  const mobileInput = useRef<HTMLInputElement>(null);
  const desktopRoot = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const debouncedTerm = useDebounce(term, 400);
  const canSearch = hasPermission("visualizar");
  const search = useGlobalSearch(debouncedTerm, canSearch && open);
  const groups = useMemo(() => search.data ?? [], [search.data]);
  const visibleResults = useMemo(
    () => groups.flatMap((group) => expanded ? group.results : group.results.slice(0, 4)),
    [expanded, groups],
  );

  useEffect(() => {
    function handleShortcut(event: globalThis.KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
    }
    function handleOutside(event: MouseEvent) {
      if (desktopRoot.current && !desktopRoot.current.contains(event.target as Node)) setOpen(false);
    }
    window.addEventListener("keydown", handleShortcut);
    document.addEventListener("mousedown", handleOutside);
    return () => {
      window.removeEventListener("keydown", handleShortcut);
      document.removeEventListener("mousedown", handleOutside);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => {
      const input = window.matchMedia("(min-width: 768px)").matches ? desktopInput.current : mobileInput.current;
      input?.focus();
    }, 0);
  }, [open]);

  function close() {
    setOpen(false);
    setExpanded(false);
    setSelectedIndex(0);
  }

  function selectResult(result: GlobalSearchResult) {
    close();
    setTerm("");
    navigate(result.href);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (!visibleResults.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelectedIndex((current) => (current + 1) % visibleResults.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedIndex((current) => (current - 1 + visibleResults.length) % visibleResults.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const selected = visibleResults[selectedIndex];
      if (selected) selectResult(selected);
    }
  }

  const panel = (
    <SearchPanel
      groups={groups}
      loading={search.isLoading || term !== debouncedTerm}
      error={search.isError}
      term={term}
      selectedKey={visibleResults[selectedIndex] ? `${visibleResults[selectedIndex].category}-${visibleResults[selectedIndex].id}` : undefined}
      expanded={expanded}
      onExpand={() => setExpanded(true)}
      onSelect={selectResult}
    />
  );

  return (
    <>
      <Button className="md:hidden" size="icon" variant="ghost" onClick={() => setOpen(true)} aria-label="Abrir pesquisa global">
        <Search className="size-[18px]" />
      </Button>

      <div className="relative hidden w-full max-w-xl md:block" ref={desktopRoot}>
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={desktopInput}
          value={term}
          onFocus={() => setOpen(true)}
          onChange={(event) => { setTerm(event.target.value); setExpanded(false); setSelectedIndex(0); setOpen(true); }}
          onKeyDown={handleKeyDown}
          className="h-10 bg-muted/50 pl-9 pr-16 shadow-inner transition focus-visible:bg-background"
          placeholder="Buscar em todo o sistema..."
          aria-label="Pesquisa global"
          role="combobox"
          aria-expanded={open}
          aria-controls="global-search-results"
        />
        <span className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 items-center gap-1 rounded border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground lg:flex">
          <Command className="size-3" /> K
        </span>
        {open && (
          <div id="global-search-results" className="absolute left-0 right-0 top-[calc(100%+10px)] z-50 overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-2xl">
            {panel}
            <div className="flex items-center gap-3 border-t bg-muted/35 px-4 py-2 text-[10px] text-muted-foreground">
              <span>↑↓ navegar</span><span>Enter abrir</span><span>Esc fechar</span>
            </div>
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] bg-slate-950/55 p-3 pt-[max(1rem,env(safe-area-inset-top))] backdrop-blur-sm md:hidden" onMouseDown={close}>
          <div className="mx-auto max-w-lg overflow-hidden rounded-2xl border bg-popover shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
            <div className="flex items-center gap-2 border-b p-3">
              <Search className="ml-1 size-4 text-muted-foreground" />
              <Input
                ref={mobileInput}
                value={term}
                onChange={(event) => { setTerm(event.target.value); setExpanded(false); setSelectedIndex(0); }}
                onKeyDown={handleKeyDown}
                className="h-10 flex-1 border-0 bg-transparent px-1 shadow-none focus-visible:ring-0"
                placeholder="O que você procura?"
                aria-label="Pesquisa global"
              />
              <Button size="icon" variant="ghost" onClick={close} aria-label="Fechar pesquisa"><X className="size-4" /></Button>
            </div>
            {panel}
          </div>
        </div>
      )}
    </>
  );
}
